import React, { type ComponentProps } from 'react'
import {
  SortableContext,
  useSortable,
  horizontalListSortingStrategy,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { DndContext } from '@dnd-kit/core'
import { restrictToHorizontalAxis, restrictToVerticalAxis } from '@dnd-kit/modifiers'

import type { Color } from '../../types/common'

import { ColorItem } from '../ColorItem'
import { Tooltip } from '../Tooltip'
import * as S from './styled'
import type { ColorwayItemProps, ColorwayProps } from './types'
import { getSelected } from './utils'

const TooltipContentForDraggable = ({ label }: { label: string }) => {
  return (
    <S.TooltipContentForDraggable>
      <S.TooltipColorName>{label} </S.TooltipColorName>
      <S.TooltipDragAndDropDescription>Drag to change order</S.TooltipDragAndDropDescription>
    </S.TooltipContentForDraggable>
  )
}

const ColorwayItem = ({
  item,
  size,
  multiselect,
  isSelected,
  direction,
  disabled,
  onClick,
  onHover,
  tooltipContent,
}: ColorwayItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isSorting } = useSortable({
    id: item.label,
    disabled,
  })

  return (
    <Tooltip
      reference={
        <button
          type="button"
          ref={setNodeRef}
          style={{
            transform: CSS.Transform.toString(transform),
            transition,
            display: 'block',
          }}
          {...attributes}
          {...listeners}
          // NOTE: This makes clicking work.
          onPointerDown={(e) => {
            onClick(item)

            listeners?.onPointerDown(e)
          }}
          onClick={() => onClick(item)}
          onMouseEnter={onHover ? () => onHover(item) : undefined}
          data-testid="Colorway.Item"
        >
          <ColorItem
            size={size}
            color={item}
            selected={isSelected}
            appearance={multiselect ? 'multi-select' : undefined}
            hoverable
          />
        </button>
      }
      placement={direction === 'vertical' ? 'left' : 'top'}
      offset={multiselect ? undefined : [0, 14]}
    >
      {isSorting ? null : tooltipContent}
    </Tooltip>
  )
}

export const Colorway = ({
  colors,
  direction = 'horizontal',
  isDraggable,
  size = 'medium',
  activeColor,
  multiselect,
  onColorHover,
  onChange,
  onSortChange,
}: ColorwayProps) => {
  if (colors.length === 0) return null

  const getIndex = (id: string) => {
    return colors.map((color) => color.label).indexOf(id)
  }

  const handleDragEnd: ComponentProps<typeof DndContext>['onDragEnd'] = (event) => {
    if (!onSortChange) return

    if (!event.over) return null

    onSortChange(getIndex(`${event.active.id}`), getIndex(`${event.over.id}`))
  }

  const handleClick = (color: Color) => {
    if (!multiselect && getSelected(color.label, activeColor)) return

    if (!onChange) return

    onChange(color)
  }

  const sortableSettings = {
    horizontal: { modifier: restrictToHorizontalAxis, strategy: horizontalListSortingStrategy },
    vertical: { modifier: restrictToVerticalAxis, strategy: verticalListSortingStrategy },
  }[direction]

  return (
    <DndContext modifiers={[sortableSettings.modifier]} onDragEnd={handleDragEnd}>
      <SortableContext
        items={colors.map((color) => color.label)}
        strategy={sortableSettings.strategy}
      >
        <S.Container direction={direction}>
          {colors.map((item, index) => (
            <div key={`${item.label}-${item.value}-${index}`} style={{ position: 'relative' }}>
              <S.Backdrop />
              <ColorwayItem
                item={item}
                size={size}
                isSelected={getSelected(item.label, activeColor)}
                multiselect={multiselect}
                disabled={!onSortChange}
                direction={direction}
                onClick={handleClick}
                onHover={onColorHover}
                tooltipContent={
                  isDraggable ? (
                    <TooltipContentForDraggable label={item.label} />
                  ) : (
                    <span style={{ whiteSpace: 'nowrap' }}>{item.label}</span>
                  )
                }
              />
            </div>
          ))}
        </S.Container>
      </SortableContext>
    </DndContext>
  )
}
